import React, { useState } from 'react';
import { Music, Plus, Minus, Trash2 } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import type { MusicSpan } from '../../services/studioService';

const MOODS = ['Warm', 'Upbeat', 'Calm', 'Epic', 'Playful'];
const CARD_W = 184; // must match StudioCanvas card width
const GAP = 36;     // must match the connector width (w-9)

const chip = (on: boolean) => on
  ? 'rounded-full border border-secondary-900 dark:border-white bg-secondary-100 dark:bg-secondary-800 px-2 py-0.5 text-[11px] font-semibold text-secondary-900 dark:text-white'
  : 'rounded-full border border-secondary-200 dark:border-secondary-700 px-2 py-0.5 text-[11px] text-secondary-600 dark:text-secondary-300 hover:border-secondary-400';

const Stepper: React.FC<{ label: string; onDec: () => void; onInc: () => void }> = ({ label, onDec, onInc }) => (
  <span className="inline-flex items-center gap-1">
    <button type="button" onClick={onDec} className="rounded border border-secondary-200 dark:border-secondary-700 p-0.5 text-secondary-500 hover:border-secondary-400"><Minus className="h-3 w-3" /></button>
    <span className="w-4 text-center font-semibold text-secondary-700 dark:text-secondary-200">{label}</span>
    <button type="button" onClick={onInc} className="rounded border border-secondary-200 dark:border-secondary-700 p-0.5 text-secondary-500 hover:border-secondary-400"><Plus className="h-3 w-3" /></button>
  </span>
);

interface Props {
  spans: MusicSpan[];
  shotCount: number;
  onChange: (spans: MusicSpan[]) => void;
}

/**
 * MusicLane — the audio LANE beneath the story-flow cards. Music is a set of SPANNING bars: each covers shots
 * N→M ("music across more than one shot"); a gap between bars is silence. This is the logical-block-timeline model
 * (not frame-by-frame) — each bar maps to one audio keyframe at render. Bars align to the shot columns above;
 * a span is added/resized with steppers (touch-friendly), never overlapping a neighbour.
 */
const MusicLane: React.FC<Props> = ({ spans, shotCount, onChange }) => {
  const [sel, setSel] = useState<number | null>(null);
  if (shotCount <= 0) return null;

  const covered = new Set<number>();
  spans.forEach((s) => { for (let i = s.from_shot; i <= s.to_shot; i++) covered.add(i); });

  const firstUncovered = () => { for (let i = 0; i < shotCount; i++) if (!covered.has(i)) return i; return -1; };
  const addSpan = () => {
    const i = firstUncovered();
    if (i < 0) return;
    const next = [...spans, { from_shot: i, to_shot: i, kind: 'instrumental' as const, mood: null }].sort((a, b) => a.from_shot - b.from_shot);
    onChange(next);
    setSel(next.findIndex((s) => s.from_shot === i && s.to_shot === i));
  };
  const update = (idx: number, patch: Partial<MusicSpan>) => onChange(spans.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const remove = (idx: number) => { onChange(spans.filter((_, i) => i !== idx)); setSel(null); };

  // The shots a span may grow into without overlapping its neighbours.
  const bounds = (idx: number) => {
    const s = spans[idx];
    let min = 0; let max = shotCount - 1;
    spans.forEach((o, i) => {
      if (i === idx) return;
      if (o.to_shot < s.from_shot) min = Math.max(min, o.to_shot + 1);
      if (o.from_shot > s.to_shot) max = Math.min(max, o.from_shot - 1);
    });
    return { min, max };
  };

  const laneWidth = shotCount * CARD_W + (shotCount - 1) * GAP;
  const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: `repeat(${shotCount}, ${CARD_W}px)`, columnGap: `${GAP}px`, width: laneWidth };
  const s = sel !== null ? spans[sel] : null;
  const b = sel !== null ? bounds(sel) : { min: 0, max: 0 };
  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center gap-2">
        <Tooltip content="Score your video by section: each bar is a music track that spans one or more shots. A gap between bars is silence.">
          <span className="inline-flex cursor-help items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-secondary-400 dark:text-secondary-500"><Music className="h-3 w-3" /> Music</span>
        </Tooltip>
        <Tooltip content="Add a music track over the next open shots — resize it to cover more">
          <button type="button" onClick={addSpan} className="inline-flex items-center gap-0.5 text-[11px] text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200"><Plus className="h-3 w-3" /> Add a span</button>
        </Tooltip>
      </div>
      <div style={gridStyle}>
        {Array.from({ length: shotCount }).map((_, i) => (covered.has(i) ? null : (
          <button
            key={`e${i}`}
            type="button"
            onClick={addSpan}
            style={{ gridColumn: `${i + 1} / ${i + 2}` }}
            className="h-7 rounded-lg border border-dashed border-secondary-200 dark:border-secondary-700 text-[11px] text-secondary-300 dark:text-secondary-600 hover:border-secondary-400"
          >
            silent
          </button>
        )))}
        {spans.map((sp, idx) => (
          <button
            key={`s${idx}`}
            type="button"
            title={`${sp.kind === 'song' ? 'Song' : 'Instrumental'}${sp.mood ? ` · ${sp.mood}` : ''} over shots ${sp.from_shot + 1}–${sp.to_shot + 1} — tap to edit`}
            onClick={() => setSel(sel === idx ? null : idx)}
            style={{ gridColumn: `${sp.from_shot + 1} / ${sp.to_shot + 2}` }}
            className={`h-7 truncate rounded-lg px-2 text-[11px] font-semibold ${sel === idx ? 'border border-secondary-900 dark:border-white bg-secondary-100 text-secondary-900 dark:bg-secondary-800 dark:text-white' : 'bg-secondary-800 text-white dark:bg-secondary-700'}`}
          >
            {sp.kind === 'song' ? 'Song' : 'Music'}{sp.mood ? ` · ${sp.mood}` : ''}
          </button>
        ))}
      </div>
      {s && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-secondary-200 dark:border-secondary-700 p-2 text-[11px]">
          <span className="text-secondary-400">Shots</span>
          <Stepper label={`${s.from_shot + 1}`} onDec={() => update(sel!, { from_shot: Math.max(b.min, s.from_shot - 1) })} onInc={() => update(sel!, { from_shot: Math.min(s.to_shot, s.from_shot + 1) })} />
          <span className="text-secondary-400">–</span>
          <Stepper label={`${s.to_shot + 1}`} onDec={() => update(sel!, { to_shot: Math.max(s.from_shot, s.to_shot - 1) })} onInc={() => update(sel!, { to_shot: Math.min(b.max, s.to_shot + 1) })} />
          <div className="mx-1 h-4 w-px bg-secondary-200 dark:bg-secondary-700" />
          <button type="button" onClick={() => update(sel!, { kind: s.kind === 'song' ? 'instrumental' : 'song' })} className={chip(false)}>{s.kind === 'song' ? 'Song' : 'Instrumental'}</button>
          {MOODS.map((m) => <button key={m} type="button" onClick={() => update(sel!, { mood: s.mood === m ? null : m })} className={chip(s.mood === m)}>{m}</button>)}
          <button type="button" onClick={() => remove(sel!)} className="ml-auto inline-flex items-center gap-0.5 text-secondary-500 hover:text-secondary-800 dark:hover:text-white"><Trash2 className="h-3 w-3" /> Remove</button>
        </div>
      )}
    </div>
  );
};

export default MusicLane;
